import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  convertAmount,
  displayTemperature,
  formatQuantity,
  pluralCount,
  type Amount,
  type CookMethod,
  type UnitSystem,
} from '@dishly/shared';
import { usePrefs } from '../stores/prefs';

export function formatDuration(minutes: number, t: TFunction): string {
  if (minutes < 1) return t('time.seconds', { count: Math.max(1, Math.round(minutes * 60)) });
  const hours = Math.floor(minutes / 60);
  const rest = Math.round(minutes % 60);
  if (!hours) return t('time.minutes', { count: rest });
  if (!rest) return t('time.hours', { count: hours });
  return t('time.hoursMinutes', { hours, minutes: rest });
}

export function formatAmount(amount: Amount, units: UnitSystem, locale: string, t: TFunction, scale = 1): string {
  const converted = convertAmount({ quantity: amount.quantity * scale, unit: amount.unit }, units);
  const number = formatQuantity(converted, locale);
  return converted.unit ? `${number} ${t(`units.${converted.unit}`, { count: pluralCount(converted.quantity) })}` : number;
}

/** Formatting that follows the reader's language and unit preference. */
export function useFormat() {
  const { t, i18n } = useTranslation();
  const units = usePrefs((s) => s.units);
  const locale = i18n.language;

  return useMemo(
    () => ({
      units,
      locale,
      amount: (amount: Amount, scale = 1) => formatAmount(amount, units, locale, t, scale),
      amounts: (amounts: Amount[], scale = 1) => amounts.map((a) => formatAmount(a, units, locale, t, scale)).join(' + '),
      temperature(celsius: number, method: CookMethod | null) {
        const { value, unit } = displayTemperature(celsius, units, method === 'oven' || method === 'air_fryer');
        return `${new Intl.NumberFormat(locale).format(value)} °${unit}`;
      },
      duration: (minutes: number) => formatDuration(minutes, t),
      number: (value: number, digits = 1) => new Intl.NumberFormat(locale, { maximumFractionDigits: digits }).format(value),
      date: (iso: string) => new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(iso)),
    }),
    [t, units, locale],
  );
}
